import { posChangedField, posField } from 'src/utils/posFieldUtils';
import {
  DeliveryType,
  Event,
  Performer,
  TicketGroupInput,
  Venue,
} from 'src/WebApiController';

/**
 * Builds a fresh `TicketGroupInput` for a newly-added event, with the same
 * defaults the manual event-search path uses (`SearchPurchasedEvents.onEventsSelect`).
 * Shared so that path and the "create PO from an event group" path produce
 * identical ticket groups. For an unallocated placeholder event, `event.virtId`
 * falls back to its POS event id and `viagId`/`mpId` are null.
 */
export function buildTicketGroupInput({
  event,
  performer,
  venue,
  currencyCode,
  ticketGroupId,
}: {
  event: Event;
  performer?: Performer | null;
  venue?: Venue | null;
  currencyCode: string;
  ticketGroupId: number;
}): TicketGroupInput {
  const eventMappingId =
    event.mpId ?? (event.viagId == null ? event.virtId : null);
  const updateEvent = {
    ...event,
    mpId: eventMappingId,
  };
  return {
    ticketGroupId,
    deliveryType: posChangedField(DeliveryType.InApp),
    currencyCode: posChangedField(currencyCode),
    viagogoVirtualId: event.virtId,
    eventMappingId: eventMappingId,
    viagogoEventId: event.viagId,
    posEventId: event.posId,
    event: updateEvent,
    expectedValue: posChangedField(null),
    faceValueCost: posField(null),
    row: posChangedField(null),
    section: posChangedField(null),
    listingNotes: posChangedField(null),
    numberOfTickets: posChangedField(null),
    unitCost: posChangedField(null),
    unitFullyLoadedCost: null,
    totalCost: posChangedField(null),
    performer: performer ?? null,
    venue: venue ?? null,
    taxPaid: posChangedField(1), // This is to turn off taxExempt by default
  } as TicketGroupInput;
}

/**
 * Builds a NEW ticket group copied from `source` ("Copy tickets") for the chosen
 * event: keeps seating/cost/quantity/notes/delivery, re-points it at `event`,
 * gets a fresh `ticketGroupId`, and clears the source's ticket artifacts so the
 * copy starts unfulfilled.
 */
export function copyTicketGroupToEvent({
  source,
  event,
  performer,
  venue,
  currencyCode,
  ticketGroupId,
  unitCostOverride,
  faceValueCostOverride,
}: {
  source: TicketGroupInput;
  event: Event;
  performer?: Performer | null;
  venue?: Venue | null;
  currencyCode: string;
  ticketGroupId: number;
  unitCostOverride?: number | null;
  faceValueCostOverride?: number | null;
}): TicketGroupInput {
  const unitCost =
    unitCostOverride !== undefined
      ? unitCostOverride
      : (source.unitCost?.value ?? null);
  const numberOfTickets = source.numberOfTickets?.value ?? null;
  const totalCost =
    unitCost != null && numberOfTickets != null
      ? unitCost * numberOfTickets
      : unitCostOverride !== undefined
        ? unitCostOverride
        : (source.totalCost?.value ?? null);
  return {
    ...source,
    numberOfTickets: posChangedField(numberOfTickets),
    unitCost: posChangedField(unitCost),
    unitFullyLoadedCost: source.unitFullyLoadedCost ?? null,
    totalCost: posChangedField(totalCost),
    expectedValue: posChangedField(source.expectedValue?.value ?? null),
    faceValueCost: posField(
      faceValueCostOverride ?? source.faceValueCost?.value ?? null
    ),
    row: posChangedField(source.row?.value ?? null),
    section: posChangedField(source.section?.value ?? null),
    listingNotes: posChangedField(source.listingNotes?.value ?? null),
    deliveryType: posChangedField(
      source.deliveryType?.value ?? DeliveryType.InApp
    ),
    ticketGroupId,
    viagogoVirtualId: event.virtId,
    eventMappingId: event.mpId,
    viagogoEventId: event.viagId,
    posEventId: event.posId,
    event,
    performer: performer ?? null,
    venue: venue ?? null,
    currencyCode: posChangedField(currencyCode),
    // Start the copy unfulfilled — don't carry the source's ticket artifacts.
    // Use an empty array (not undefined): the backend's IsPredelivered() calls
    // .Any() on the tickets list and throws on null.
    tickets: posField([]),
  } as TicketGroupInput;
}
